import { mockTemplates } from '../../data/mockTemplates';
// import { apiFetch } from './client'; // <- uncomment when wiring up the real backend

/**
 * Templates service. Templates live in app storage (not Google
 * Calendar) even in the final version, but are still routed through a
 * services/api module so they can move to a real backend (e.g. a
 * Cloudflare D1/KV-backed Worker endpoint) without touching
 * components.
 *
 * Persistence: the full list (both the original curated templates
 * from mockTemplates.js and anything the user has created/edited) is
 * mirrored to localStorage on every create/update/delete, and read
 * back from there on startup. This is what makes user-created
 * templates survive closing the app / restarting the browser —
 * previously `templates` was only an in-memory module variable that
 * reset to mockTemplates on every reload.
 *
 * On the very first run (nothing in localStorage yet), the list is
 * seeded from mockTemplates so the two "coexist" from the start: the
 * initial curated templates and anything the user adds afterwards all
 * live in the same persisted array, indistinguishable from each other
 * once saved.
 */
const STORAGE_KEY = 'family-calendar:templates';

function loadTemplates() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (err) {
    console.error('[templatesApi] localStorageからの読み込みに失敗したため、初期テンプレートから開始します。', err);
  }
  // 初回起動、または保存データが壊れていた場合は初期テンプレートから始める。
  // ここで即座にlocalStorageへも書き込み、次回起動時からは完全に
  // localStorage側が正となるようにする（初期テンプレートとユーザー作成
  // テンプレートが同じ1つの永続化された配列として共存する）。
  const seeded = [...mockTemplates];
  saveTemplates(seeded);
  return seeded;
}

function saveTemplates(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (err) {
    // 保存に失敗しても（プライベートブラウジング等でlocalStorageが使えない
    // 場合など）アプリ自体は動作を続けられるよう、ここでは投げずに警告のみ。
    console.error('[templatesApi] localStorageへの保存に失敗しました。', err);
  }
}

let templates = loadTemplates();

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export async function getTemplates() {
  await delay(100);
  return [...templates];
}

export async function createTemplate(templateData) {
  await delay(120);
  const newTemplate = { id: `tpl-${Date.now()}`, ...templateData };
  templates = [...templates, newTemplate];
  saveTemplates(templates);
  return newTemplate;
}

export async function updateTemplate(id, updates) {
  await delay(120);
  templates = templates.map((t) => (t.id === id ? { ...t, ...updates } : t));
  saveTemplates(templates);
  return templates.find((t) => t.id === id) ?? null;
}

export async function deleteTemplate(id) {
  await delay(100);
  templates = templates.filter((t) => t.id !== id);
  saveTemplates(templates);
  return true;
}
